import { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { ChevronLeft, X, Calendar } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
      initInlineWidget: (options: { url: string; parentElement: HTMLElement }) => void;
    };
  }
}

const CALENDLY_URL = 'https://calendly.com/pitech/30min?hide_event_type_details=1&hide_gdpr_banner=1';

interface CalendlyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CalendlyModal = ({ open, onOpenChange }: CalendlyModalProps) => {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setHasError(false);

      // Load Calendly scripts
      if (!document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        script.onerror = () => {
          setHasError(true);
          setIsLoading(false);
        };
        document.head.appendChild(script);
      }

      if (!document.querySelector('link[href="https://assets.calendly.com/assets/external/widget.css"]')) {
        const link = document.createElement('link');
        link.href = 'https://assets.calendly.com/assets/external/widget.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setHasError(false);

      const initCalendly = () => {
        const container = document.getElementById('calendly-inline-container');
        if (container && window.Calendly) {
          container.innerHTML = '';
          window.Calendly.initInlineWidget({
            url: CALENDLY_URL,
            parentElement: container,
          });
          setIsLoading(false);
          return true;
        }
        return false;
      };

      // Try immediately, then retry with intervals
      if (!initCalendly()) {
        let attempts = 0;
        const maxAttempts = 20;
        const interval = setInterval(() => {
          attempts++;
          if (initCalendly()) {
            clearInterval(interval);
            return;
          }
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            setHasError(true);
            setIsLoading(false);
          }
        }, 300);
        return () => clearInterval(interval);
      }
    }
  }, [open]);

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="h-[90vh] max-h-[90vh]">
          <DrawerHeader className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <DrawerClose asChild>
              <button className="flex items-center gap-1 text-primary font-medium">
                <ChevronLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
            </DrawerClose>
            <DrawerTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span>Free Consultation</span>
            </DrawerTitle>
            <div className="w-12" />
          </DrawerHeader>
          <div className="flex-1 overflow-hidden h-[calc(90vh-80px)]">
            {hasError ? (
              <div className="flex items-center justify-center h-full p-6 text-center">
                <div className="max-w-sm">
                  <p className="text-sm text-muted-foreground">
                    The calendar couldn’t load here. Please open it in a new tab.
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                  >
                    Open Calendly
                  </a>
                </div>
              </div>
            ) : (
              <>
                {isLoading && (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
                      <p className="text-sm text-muted-foreground">Loading calendar...</p>
                    </div>
                  </div>
                )}
                <div id="calendly-inline-container" className="w-full h-full" style={{ minWidth: '320px' }} />
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop version
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={() => onOpenChange(false)}
          />
          <div className="fixed inset-0 z-[101] flex items-center justify-center pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="w-[95%] max-w-2xl h-[85vh] bg-background rounded-2xl pointer-events-auto overflow-hidden shadow-2xl"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-background/95 backdrop-blur-xl">
                <button onClick={() => onOpenChange(false)} className="flex items-center gap-1 text-muted-foreground font-medium hover:text-foreground transition-colors">
                  Cancel
                </button>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-foreground">Free Consultation</span>
                </div>
                <button 
                  onClick={() => onOpenChange(false)}
                  className="w-8 h-8 rounded-full bg-muted/80 flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="h-[calc(85vh-60px)] overflow-hidden">
                {isLoading && (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
                      <p className="text-sm text-muted-foreground">Loading calendar...</p>
                    </div>
                  </div>
                )}
                <div id="calendly-inline-container" className="w-full h-full" style={{ minWidth: '320px' }} />
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

// Context for Calendly modal
interface CalendlyContextType {
  openCalendly: () => void;
}

const CalendlyContext = createContext<CalendlyContextType | undefined>(undefined);

export const CalendlyProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);

  return (
    <CalendlyContext.Provider value={{ openCalendly: () => setOpen(true) }}>
      {children}
      <CalendlyModal open={open} onOpenChange={setOpen} />
    </CalendlyContext.Provider>
  );
};

export const useCalendly = () => {
  const context = useContext(CalendlyContext);
  if (!context) {
    throw new Error('useCalendly must be used within CalendlyProvider');
  }
  return context;
};

export default useCalendly;
